import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import type { Branch, BranchesResponse } from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, PageTitle, useSubmit } from "../components/ui";
import { api, errorText, send } from "../lib/api";
import { useCan, useOrg } from "../lib/org";
import { useMe } from "../lib/session";

export function BranchesPage() {
  const { data: me } = useMe();
  const can = useCan();
  const [editing, setEditing] = useState<Branch | "new" | null>(null);
  const branches = useQuery({
    queryKey: ["branches", me?.activeRole],
    queryFn: () => api<BranchesResponse>("/v1/branches"),
  });

  return (
    <>
      <PageTitle
        title="المجمعات"
        sub="فروع الجمعية وحلقاتها وطلابها"
        actions={
          can.createBranch && (
            <button className="btn btn-primary" onClick={() => setEditing("new")}>
              <Plus size={18} strokeWidth={1.75} /> مجمع جديد
            </button>
          )
        }
      />
      <section className="card">
        {branches.isPending ? (
          <Loading />
        ) : branches.isError ? (
          <Empty title="تعذر التحميل">{errorText(branches.error)}</Empty>
        ) : branches.data.branches.length === 0 ? (
          <Empty title="لا توجد مجمعات بعد">
            {can.createBranch ? "أضف أول مجمع لتبدأ بإنشاء الحلقات." : "لا توجد مجمعات ضمن نطاقك."}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>المجمع</th>
                  <th>الرمز</th>
                  <th>المدينة</th>
                  <th>الحلقات</th>
                  <th>الطلاب</th>
                  <th>طلبات الانضمام</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {branches.data.branches.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <Link to="/circles" search={{ branchId: b.id }}>
                        {b.name}
                      </Link>{" "}
                      {b.archivedAt && <Badge>مؤرشف</Badge>}
                    </td>
                    <td>
                      <span className="ltr">{b.code}</span>
                    </td>
                    <td>{b.city ?? <span className="muted">—</span>}</td>
                    <td className="num">{b.circleCount}</td>
                    <td className="num">{b.studentCount}</td>
                    <td>
                      {b.acceptsJoinRequests ? <Badge tone="success">مفتوحة</Badge> : <Badge>مغلقة</Badge>}
                    </td>
                    <td className="actions">
                      {can.has("branches.manage") && (
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(b)}>
                          تعديل
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <BranchForm branch={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function BranchForm({ branch, onClose }: { branch: Branch | "new" | null; onClose: () => void }) {
  const qc = useQueryClient();
  const org = useOrg();
  const isNew = branch === "new";
  const current = branch && branch !== "new" ? branch : null;
  const { busy, error, run } = useSubmit();
  const [form, setForm] = useState({ name: "", code: "", city: "", acceptsJoinRequests: true });
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const key = current?.id ?? (isNew ? "new" : null);
  if (key !== loadedFor) {
    setLoadedFor(key);
    setForm({
      name: current?.name ?? "",
      code: current?.code ?? "",
      city: current?.city ?? "",
      acceptsJoinRequests: current?.acceptsJoinRequests ?? true,
    });
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      if (isNew) {
        if (!org) throw new Error("no organization");
        await send("POST", "/v1/branches", {
          organizationId: org.id,
          name: form.name,
          code: form.code,
          city: form.city || undefined,
        });
      } else if (current) {
        await send("PATCH", `/v1/branches/${current.id}`, {
          name: form.name,
          code: form.code,
          city: form.city || null,
          acceptsJoinRequests: form.acceptsJoinRequests,
        });
      }
      await qc.invalidateQueries({ queryKey: ["branches"] });
      await qc.invalidateQueries({ queryKey: ["overview"] });
      onClose();
    }, errorText);
  }

  async function toggleArchive() {
    if (!current) return;
    await run(async () => {
      await send("PATCH", `/v1/branches/${current.id}`, { archived: !current.archivedAt });
      await qc.invalidateQueries({ queryKey: ["branches"] });
      onClose();
    }, errorText);
  }

  return (
    <Modal title={isNew ? "مجمع جديد" : "تعديل المجمع"} open={branch !== null} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <Field id="b-name" label="اسم المجمع">
          <input
            id="b-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            minLength={2}
          />
        </Field>
        <div className="form-row">
          <Field id="b-code" label="الرمز" hint="قصير وفريد، مثل RYD-1">
            <input
              id="b-code"
              dir="ltr"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              required
            />
          </Field>
          <Field id="b-city" label="المدينة">
            <input
              id="b-city"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </Field>
        </div>
        {!isNew && (
          <label className="check">
            <input
              type="checkbox"
              checked={form.acceptsJoinRequests}
              onChange={(e) => setForm({ ...form, acceptsJoinRequests: e.target.checked })}
            />
            يستقبل طلبات الانضمام من صفحة التسجيل العامة
          </label>
        )}
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || form.name.length < 2 || !form.code}>
            {busy ? "جارٍ الحفظ…" : "حفظ"}
          </button>
          {current && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => void toggleArchive()}
              disabled={busy}
            >
              {current.archivedAt ? "إلغاء الأرشفة" : "أرشفة المجمع"}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}
